import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styles from '../journey-explorer.module.scss';
import { JourneyCompare, JourneyMeta } from '../../../../models/journey-explorer/journey-explorer';
import { CompareDimension, compareDimensions } from '../journey-compare';

interface CompareControlProps {
	meta: JourneyMeta;
	compare: JourneyCompare | null;
	onChange: (compare: JourneyCompare | null) => void;
}

const draftFor = (dimension: CompareDimension): JourneyCompare => ({
	column: dimension.column,
	a: dimension.values[0],
	b: dimension.values[1],
});

const CompareControl: FC<CompareControlProps> = ({ meta, compare, onChange }) => {
	const dimensions = useMemo(() => compareDimensions(meta), [meta]);
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState<JourneyCompare | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) {
			return;
		}
		const onDown = (event: MouseEvent) => {
			if (!containerRef.current?.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
		window.addEventListener('mousedown', onDown);
		window.addEventListener('keydown', onKey);
		return () => {
			window.removeEventListener('mousedown', onDown);
			window.removeEventListener('keydown', onKey);
		};
	}, [open]);

	if (!dimensions.length) {
		return null;
	}

	const toggle = () => {
		setDraft(compare ?? draftFor(dimensions[0]));
		setOpen((value) => !value);
	};
	const dimension = dimensions.find((item) => item.column === draft?.column) ?? dimensions[0];
	const valueSelect = (value: string, update: (next: string) => void) => (
		<select className={styles.flow_select} value={value} onChange={(event) => update(event.target.value)}>
			{dimension.values.map((item) => (
				<option key={item} value={item}>
					{item}
				</option>
			))}
		</select>
	);

	return (
		<div ref={containerRef} className={styles.compare}>
			{compare ? (
				<span className={styles.compare_chip}>
					<span className={styles.compare_a}>A</span>
					<b>{compare.a}</b>
					<span className={styles.compare_vs}>vs</span>
					<span className={styles.compare_b}>B</span>
					<b>{compare.b}</b>
					<button type="button" className={styles.compare_edit} onClick={toggle}>
						Edit
					</button>
					<button
						type="button"
						className={styles.compare_stop}
						aria-label="Stop comparing"
						title="Stop comparing"
						onClick={() => onChange(null)}
					>
						✕
					</button>
				</span>
			) : (
				<button type="button" className={styles.compare_button} aria-expanded={open} onClick={toggle}>
					Compare
				</button>
			)}
			{open && draft && (
				<div className={styles.compare_popover} role="dialog" aria-label="Compare two segments">
					<div className={styles.compare_title}>Compare two segments</div>
					<div className={styles.compare_hint}>KPIs, Map, Flow and Paths show both side by side.</div>
					<div className={styles.compare_grid}>
						<span>By</span>
						<select
							className={styles.flow_select}
							value={dimension.column}
							onChange={(event) => {
								const next = dimensions.find((item) => item.column === event.target.value);
								if (next) {
									setDraft(draftFor(next));
								}
							}}
						>
							{dimensions.map((item) => (
								<option key={item.column} value={item.column}>
									{item.label}
								</option>
							))}
						</select>
						<span className={styles.compare_a}>A</span>
						{valueSelect(draft.a, (a) => setDraft({ ...draft, a }))}
						<span className={styles.compare_b}>B</span>
						{valueSelect(draft.b, (b) => setDraft({ ...draft, b }))}
					</div>
					<div className={styles.compare_actions}>
						<button
							type="button"
							className={styles.compare_apply}
							disabled={draft.a === draft.b}
							onClick={() => {
								onChange(draft);
								setOpen(false);
							}}
						>
							Compare
						</button>
						<button type="button" className={styles.compare_cancel} onClick={() => setOpen(false)}>
							Cancel
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default CompareControl;
